/**
 * CookieからXSRF-TOKENを取得
 */
const getXsrfTokenFromCookie = (): string => {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
    if (match) {
        return decodeURIComponent(match[1])
    }
    return ""
}

export const apiFunction = async <Type = object>(
    url: string, 
    data: {} = {}, 
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" = "POST"
): Promise<Type | null> => {

    // CSRFトークン: CookieのXSRF-TOKENを優先、なければmetaタグから取得
    const xsrfToken = getXsrfTokenFromCookie()
    const metaCsrfToken = document.querySelector("meta[name=\"csrf-token\"]")?.getAttribute("content") ?? ""
    const csrfToken: string = xsrfToken || metaCsrfToken;
    const authToken = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "credentials": "include",
        "X-CSRF-TOKEN": csrfToken,
        "X-XSRF-TOKEN": xsrfToken, // Sanctum用
    };
    // 認証が必要なAPIリクエストの場合、Bearerトークンを追加
    if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
    }

    const init: RequestInit = {
        method: method,
        headers: headers,
        credentials: 'include', // セッションCookieを送信するために必要
    };
    
    // POSTとPUT/PATCHの場合にbodyを追加（camelCase → snake_case変換）
    if (method !== "GET" && method !== "DELETE") {
        init.body = JSON.stringify(toSnakeCase(data))
    }
    
    const response: Response = await fetch(url, init)

    // 419エラー（CSRF token mismatch）の場合
    if (response.status === 419) {
        const error = {
            statusCode: response.status,
            isCsrfError: true,
            message: "セッションの有効期限が切れました。ページを再読み込みしてください。"
        }
        throw error
    }

    // 422エラーの場合はバリデーションエラーを返す
    if (response.status === 422) {
        const error: {
            statusCode: number
            isValidationError: boolean
            message: string
            errors: Record<string, string[]>
        } = {
            statusCode: response.status,
            isValidationError: true,
            message: "Validation Error",
            errors: {} as { [key: string]: string[] }
        }
        const json = await response.json()
        Object.entries(json.errors).forEach(([key, value]: [string, any]): void => {
            if (Array.isArray(value)) {
                // snake_caseをcamelCaseに変換
                const camelKey = toCamelCaseString(key)
                error.errors[camelKey] = value
            }
        })
        error.message = json.message
        throw error
    }

    // 500系エラーの場合はグローバルエラーイベントを発火
    if (response.status >= 500) {
        window.dispatchEvent(new CustomEvent('api-server-error', {
            detail: { statusCode: response.status }
        }))
        const error = {
            statusCode: response.status,
            isServerError: true,
            message: "Server Error"
        }
        throw error
    }

    // 200-299の範囲は成功として扱う（200 OK, 201 Created など）
    if (response.status < 200 || response.status >= 300) {
        const json = await response.json()
        const error = {
            statusCode: response.status,
            status: json.status,
            type: json.type,
            message: json.message,
            errors: json.errors
        }
        if (json.errors) {
            error.errors = toCamelCase(json.errors)
        }
        throw error
    }

    const json = await response.json()
    if (Array.isArray(json)) {
        const result: Type | unknown[] = []
        json.forEach((obj: object): void => {
            result.push(toCamelCase(obj))
        })
        return result as Type
    }

    return toCamelCase<Type>(json)
}

const toCamelCaseString = (str: string): string => {
    return str.replace(/_([a-z])/g, (_: string, group) => group.toUpperCase())
}

const toCamelCase = <Type>(object: { [key: string]: any }): Type => {
    const result: { [key: string]: any } = {}
    Object.keys(object).forEach((org: string): void => {
        const key: string = toCamelCaseString(org)

        if (Array.isArray(object[org])) {
            const arrayResult: any[] = []

            object[org].forEach((item: any): void => {
                if (item !== null && typeof item === "object") {
                    arrayResult.push(toCamelCase(item))
                } else {
                    arrayResult.push(item)
                }
            })

            result[key] = arrayResult
        } else if (object[org] !== null && typeof object[org] === "object") {
            result[key] = toCamelCase(object[org])
        } else {
            result[key] = object[org]
        }
    })
    return result as Type
}

const toSnakeCaseString = (text: string): string => {
    return text.split(/(?=[A-Z])/).map((word: string): string => {
        return word.toLowerCase()
    }).join("_")
}

const toSnakeCase = (obj: object): object => {
    return Object.entries(obj).reduce((result: object, [key, value]) => {
        let newValue = value
        if (value && typeof value === "object" && !Array.isArray(value)) {
            newValue = toSnakeCase(value)
        } else if (Array.isArray(value)) {
            newValue = value.map((item): object | any => {
                if (item && typeof item === "object" && !Array.isArray(item)) {
                    return toSnakeCase(item)
                }
                return item
            })
        }
        return { ...result, [toSnakeCaseString(key)]: newValue }
    }, {})
}
